import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {ALL_LOCATIONS_NODE_ID} from './deviceRollup';

export interface DeviceListUrlState {
  search: string;
  setSearch: (value: string) => void;
  nodeId: string;
  setNodeId: (value: string) => void;
  showOnlyRemaining: boolean;
  setShowOnlyRemaining: (value: boolean) => void;
}

/**
 * Devices-tab list state (search/tree selection/show-only-remaining) synced to
 * `?q=&node=&remaining=1`. Param names are a cross-PR contract — don't rename.
 */
export const useDeviceListUrlState = (): DeviceListUrlState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('q') ?? '';
  const nodeId = searchParams.get('node') ?? ALL_LOCATIONS_NODE_ID;
  const showOnlyRemaining = searchParams.get('remaining') === '1';

  const setParam = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          if (value === undefined || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  return useMemo(
    () => ({
      search,
      setSearch: (value: string) => setParam('q', value),
      nodeId,
      setNodeId: (value: string) =>
        setParam('node', value === ALL_LOCATIONS_NODE_ID ? undefined : value),
      showOnlyRemaining,
      setShowOnlyRemaining: (value: boolean) =>
        setParam('remaining', value ? '1' : undefined),
    }),
    [search, nodeId, showOnlyRemaining, setParam]
  );
};
